import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import useAuthStore from '../context/authStore';
import { useLoader } from '../components/shared/LoaderContext';
import toast from 'react-hot-toast';

export default function SignupPage() {
  const [form, setForm] = useState({
    firstName: '', middleName: '', lastName: '',
    email: '', mobile: '', password: '', confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { signup } = useAuthStore();
  const { showLoader, hideLoader } = useLoader();
  const navigate = useNavigate();

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirmPassword) { toast.error('Passwords do not match'); return; }
    if (form.password.length < 8) { toast.error('Password must be at least 8 characters'); return; }

    const interval = showLoader('Creating your account...');
    try {
      await signup({
        firstName: form.firstName, middleName: form.middleName,
        lastName: form.lastName, email: form.email,
        mobile: form.mobile, password: form.password,
      });
      hideLoader(interval);
      toast.success('Account created! Please complete your membership registration.');
      navigate('/register-membership');
    } catch (err) {
      hideLoader(interval);
      toast.error(err.response?.data?.message || 'Signup failed');
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f2440 0%, #1a3a5c 50%, #2563a8 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
    }}>
      <div style={{ width: '100%', maxWidth: 520 }}>
        <div style={{ textAlign: 'center', marginBottom: 28, color: 'white' }}>
          <div style={{
            width: 70, height: 70, borderRadius: '50%', background: 'white',
            overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 14px', border: '3px solid var(--accent)'
          }}>
            <img src="/logo.png" alt="BPS"
              style={{ width: '85%', height: '85%', objectFit: 'contain' }}
              onError={(e) => { e.target.style.display = 'none'; }} />
          </div>
          <h1 style={{ fontSize: 24, fontFamily: "'Playfair Display', serif" }}>
            Bombay Psychiatric Society
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 14, marginTop: 4 }}>
            Create your member account
          </p>
        </div>

        <div className="card" style={{ padding: 32 }}>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="grid-3">
              <div className="form-group">
                <label className="form-label">First Name *</label>
                <input className="form-input" value={form.firstName}
                  onChange={(e) => set('firstName', e.target.value)} required placeholder="First" />
              </div>
              <div className="form-group">
                <label className="form-label">Middle Name</label>
                <input className="form-input" value={form.middleName}
                  onChange={(e) => set('middleName', e.target.value)} placeholder="Middle" />
              </div>
              <div className="form-group">
                <label className="form-label">Last Name *</label>
                <input className="form-input" value={form.lastName}
                  onChange={(e) => set('lastName', e.target.value)} required placeholder="Last" />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Email Address * (this will be your login username)</label>
              <input type="email" className="form-input" value={form.email}
                onChange={(e) => set('email', e.target.value)} required placeholder="your@email.com" />
            </div>
            <div className="form-group">
              <label className="form-label">Mobile Number *</label>
              <input className="form-input" value={form.mobile}
                onChange={(e) => set('mobile', e.target.value.replace(/\D/g, '').slice(0, 10))}
                required placeholder="10-digit mobile number" maxLength={10} />
            </div>
            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">Password *</label>
                <div style={{ position: 'relative' }}>
                  <input type={showPassword ? 'text' : 'password'} className="form-input"
                    value={form.password} onChange={(e) => set('password', e.target.value)}
                    required placeholder="Min 8 characters" minLength={8} style={{ paddingRight: 44 }} />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} style={{
                    position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)'
                  }}>
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Confirm Password *</label>
                <div style={{ position: 'relative' }}>
                  <input type={showConfirmPassword ? 'text' : 'password'} className="form-input"
                    value={form.confirmPassword} onChange={(e) => set('confirmPassword', e.target.value)}
                    required placeholder="Repeat password" style={{ paddingRight: 44 }} />
                  <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} style={{
                    position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)'
                  }}>
                    {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
            </div>
            <button type="submit" className="btn btn-primary"
              style={{ width: '100%', justifyContent: 'center', padding: '12px', marginTop: 4 }}>
              Create Account & Continue
            </button>
          </form>
          <div style={{
            textAlign: 'center', marginTop: 20, paddingTop: 16,
            borderTop: '1px solid var(--border)', fontSize: 14, color: 'var(--text-muted)'
          }}>
            Already have an account?{' '}
            <Link to="/login" style={{ color: 'var(--primary-light)', fontWeight: 600, textDecoration: 'none' }}>
              Sign In
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
